import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, Calculator, Ruler, TriangleIcon, Info } from 'lucide-react';

/* ── Pitch factor lookup table ────────────────────────────── */
const PITCH_FACTORS = {
  0: 1.000, 1: 1.003, 2: 1.014, 3: 1.031, 4: 1.054,
  5: 1.083, 6: 1.118, 7: 1.158, 8: 1.202, 9: 1.250,
  10: 1.302, 11: 1.357, 12: 1.414, 13: 1.474, 14: 1.537,
  15: 1.601, 16: 1.667, 17: 1.734, 18: 1.803,
};

/* ── Common pitch presets ─────────────────────────────────── */
const COMMON_PITCHES = [
  { rise: 3, label: '3/12 — Low slope' },
  { rise: 4, label: '4/12 — Moderate' },
  { rise: 6, label: '6/12 — Standard' },
  { rise: 8, label: '8/12 — Steep' },
  { rise: 12, label: '12/12 — Very steep (45deg)' },
];

export default function PitchCalculator() {
  const { openAuthModal } = useAuth();
  const [rise, setRise] = useState('');
  const [run, setRun] = useState('12');
  const [flatArea, setFlatArea] = useState('');

  const calc = useMemo(() => {
    const r = parseFloat(rise);
    const ru = parseFloat(run);
    if (!r || !ru || r < 0 || ru <= 0) return null;

    const pitchPer12 = (r / ru) * 12;
    const slopeLength = Math.sqrt(r * r + ru * ru);
    const pitchFactor = slopeLength / ru;
    const angleDeg = Math.atan(r / ru) * (180 / Math.PI);
    const flatSqft = parseFloat(flatArea) || 0;
    const adjustedArea = flatSqft > 0 ? flatSqft * pitchFactor : null;

    return {
      pitchPer12: pitchPer12.toFixed(1),
      pitchFactor: pitchFactor.toFixed(3),
      angleDeg: angleDeg.toFixed(1),
      adjustedArea: adjustedArea ? adjustedArea.toFixed(0) : null,
      adjustedSquares: adjustedArea ? (adjustedArea / 100).toFixed(2) : null,
      riseNorm: r,
      runNorm: ru,
    };
  }, [rise, run, flatArea]);

  /* SVG triangle dimensions */
  const svgW = 280;
  const svgH = 200;
  const margin = 30;

  const triPoints = useMemo(() => {
    if (!calc) return null;
    const maxRun = svgW - margin * 2;
    const maxRise = svgH - margin * 2;
    const scale = Math.min(maxRun / calc.runNorm, maxRise / calc.riseNorm, maxRun / 12);
    const drawRun = calc.runNorm * scale;
    const drawRise = calc.riseNorm * scale;
    const bx = margin;
    const by = svgH - margin;
    return {
      base: `${bx},${by}`,
      right: `${bx + drawRun},${by}`,
      top: `${bx + drawRun},${by - drawRise}`,
      drawRun,
      drawRise,
      bx,
      by,
    };
  }, [calc]);

  const handlePreset = (presetRise) => {
    setRise(String(presetRise));
    setRun('12');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Roof Pitch Factor Chart &amp; Calculator — Every Pitch 1/12 to 18/12</title>
        <meta name="description" content="Full roof pitch factor chart: 4/12 = 1.054, 6/12 = 1.118, 8/12 = 1.202, 12/12 = 1.414 — plus hip/valley factors and a free calculator that converts pitch to degrees and adjusts roof area." />
        <meta property="og:title" content="Roof Pitch Factor Chart &amp; Calculator — Every Pitch 1/12 to 18/12" />
        <meta property="og:description" content="Full pitch factor chart with hip/valley factors, plus a free calculator that converts pitch to degrees and adjusts roof area." />
        <link rel="canonical" href="https://squarecount.io/tools/pitch-calculator" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Free Roof Pitch Calculator",
          "url": "https://squarecount.io/tools/pitch-calculator",
          "applicationCategory": "UtilityApplication",
          "operatingSystem": "Web Browser",
          "description": "Calculate roof pitch, slope factor multiplier, and adjusted roof area from rise and run measurements. Free online tool for roofing contractors.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          "creator": { "@type": "Organization", "name": "SquareCount.io", "url": "https://squarecount.io" }
        })}</script>
      </Helmet>
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* ── Header ────────────────────────────────────────── */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-4">
            <Calculator className="w-4 h-4" />
            <span className="font-medium text-sm">Free Tool</span>
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl text-white mb-4">
            Roof Pitch Factor Chart &amp; Calculator — Every Pitch 1/12 to 18/12
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            Calculate roof pitch multiplier, slope factor, and adjusted roof area instantly. Enter rise and run to get your pitch factor for any roof — including the common 6/12 pitch.
          </p>
        </div>

        {/* ── Calculator Card ───────────────────────────────── */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Left — Inputs */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-sm p-6 sm:p-8">
            <h2 className="font-heading font-bold text-xl text-white mb-6 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-orange-400" />
              Enter Measurements
            </h2>

            {/* Quick presets */}
            <div className="mb-6">
              <label className="block text-sm text-slate-400 mb-2">Common pitches</label>
              <div className="flex flex-wrap gap-2">
                {COMMON_PITCHES.map((p) => (
                  <button
                    key={p.rise}
                    onClick={() => handlePreset(p.rise)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      rise === String(p.rise) && run === '12'
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    {p.rise}/12
                  </button>
                ))}
              </div>
            </div>

            {/* Rise input */}
            <div className="mb-4">
              <label htmlFor="rise" className="block text-sm font-medium text-slate-300 mb-1.5">
                Rise (inches)
              </label>
              <input
                id="rise"
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={rise}
                onChange={(e) => setRise(e.target.value)}
                placeholder="e.g. 6"
                className="w-full h-12 rounded-lg bg-slate-800 border border-slate-700 px-4 text-white text-lg placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Run input */}
            <div className="mb-6">
              <label htmlFor="run" className="block text-sm font-medium text-slate-300 mb-1.5">
                Run (inches) — typically 12
              </label>
              <input
                id="run"
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={run}
                onChange={(e) => setRun(e.target.value)}
                placeholder="12"
                className="w-full h-12 rounded-lg bg-slate-800 border border-slate-700 px-4 text-white text-lg placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Flat area input (optional) */}
            <div className="mb-2 pt-4 border-t border-slate-800">
              <label htmlFor="flatArea" className="block text-sm font-medium text-slate-300 mb-1.5">
                Flat roof area (sq ft) — <span className="text-slate-500">optional</span>
              </label>
              <input
                id="flatArea"
                type="number"
                min="0"
                step="1"
                value={flatArea}
                onChange={(e) => setFlatArea(e.target.value)}
                placeholder="e.g. 2000"
                className="w-full h-12 rounded-lg bg-slate-800 border border-slate-700 px-4 text-white text-lg placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">Enter the footprint area to see the pitch-adjusted roof area.</p>
            </div>
          </div>

          {/* Right — Results */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-sm p-6 sm:p-8 flex flex-col">
            <h2 className="font-heading font-bold text-xl text-white mb-6 flex items-center gap-2">
              <TriangleIcon className="w-5 h-5 text-orange-400" />
              Results
            </h2>

            {calc ? (
              <>
                {/* Result grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/80 rounded-xl p-4 text-center">
                    <p className="text-xs uppercase text-slate-500 mb-1">Pitch</p>
                    <p className="font-heading font-bold text-3xl text-orange-400">{calc.pitchPer12}/12</p>
                  </div>
                  <div className="bg-slate-800/80 rounded-xl p-4 text-center">
                    <p className="text-xs uppercase text-slate-500 mb-1">Angle</p>
                    <p className="font-heading font-bold text-3xl text-white">{calc.angleDeg}&deg;</p>
                  </div>
                  <div className="bg-slate-800/80 rounded-xl p-4 text-center">
                    <p className="text-xs uppercase text-slate-500 mb-1">Pitch Factor</p>
                    <p className="font-heading font-bold text-3xl text-white">{calc.pitchFactor}</p>
                  </div>
                  <div className="bg-slate-800/80 rounded-xl p-4 text-center">
                    <p className="text-xs uppercase text-slate-500 mb-1">Area Increase</p>
                    <p className="font-heading font-bold text-3xl text-white">
                      +{((parseFloat(calc.pitchFactor) - 1) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Adjusted area */}
                {calc.adjustedArea && (
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-5 mb-6 text-center">
                    <p className="text-xs uppercase text-orange-400 mb-1">Adjusted Roof Area</p>
                    <p className="font-heading font-bold text-4xl text-orange-400">
                      {Number(calc.adjustedArea).toLocaleString()} sq ft
                    </p>
                    <p className="text-sm text-orange-300/70 mt-1">
                      {calc.adjustedSquares} squares
                    </p>
                  </div>
                )}

                {/* SVG diagram */}
                {triPoints && (
                  <div className="flex-1 flex items-center justify-center">
                    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full max-w-[280px]" aria-label="Roof pitch triangle diagram">
                      {/* Triangle */}
                      <polygon
                        points={`${triPoints.base} ${triPoints.right} ${triPoints.top}`}
                        fill="rgba(249,115,22,0.08)"
                        stroke="#f97316"
                        strokeWidth="2"
                      />
                      {/* Run label */}
                      <text
                        x={triPoints.bx + triPoints.drawRun / 2}
                        y={triPoints.by + 18}
                        textAnchor="middle"
                        fill="#94a3b8"
                        fontSize="12"
                        fontFamily="monospace"
                      >
                        Run: {run}&quot;
                      </text>
                      {/* Rise label */}
                      <text
                        x={triPoints.bx + triPoints.drawRun + 14}
                        y={triPoints.by - triPoints.drawRise / 2}
                        textAnchor="start"
                        fill="#94a3b8"
                        fontSize="12"
                        fontFamily="monospace"
                      >
                        Rise: {rise}&quot;
                      </text>
                      {/* Angle arc */}
                      <path
                        d={`M ${triPoints.bx + 30},${triPoints.by} A 30 30 0 0 0 ${triPoints.bx + 30 * Math.cos(Math.atan(calc.riseNorm / calc.runNorm))},${triPoints.by - 30 * Math.sin(Math.atan(calc.riseNorm / calc.runNorm))}`}
                        fill="none"
                        stroke="#f97316"
                        strokeWidth="1.5"
                        strokeDasharray="3 2"
                      />
                      <text
                        x={triPoints.bx + 38}
                        y={triPoints.by - 8}
                        fill="#f97316"
                        fontSize="11"
                        fontFamily="monospace"
                      >
                        {calc.angleDeg}&deg;
                      </text>
                    </svg>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-600">
                  <TriangleIcon className="w-16 h-16 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Enter rise and run to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Pitch Factor Reference Table ──────────────────── */}
        <section className="mb-16">
          <h2 className="font-heading font-bold text-2xl text-white mb-6 text-center">
            Roof Pitch Factor Chart (1/12 &ndash; 18/12)
          </h2>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="px-4 py-3 text-left font-heading font-bold text-slate-400 uppercase">Pitch</th>
                  <th className="px-4 py-3 text-center font-heading font-bold text-slate-400 uppercase">Angle (degrees)</th>
                  <th className="px-4 py-3 text-center font-heading font-bold text-slate-400 uppercase">Pitch Factor</th>
                  <th className="px-4 py-3 text-center font-heading font-bold text-slate-400 uppercase">Hip/Valley Factor</th>
                  <th className="px-4 py-3 text-center font-heading font-bold text-slate-400 uppercase hidden sm:table-cell">Area Increase</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(PITCH_FACTORS).map(([pitch, factor]) => {
                  const p = parseInt(pitch);
                  const angle = (Math.atan(p / 12) * 180 / Math.PI).toFixed(1);
                  const hipValley = (Math.sqrt(p * p + 288) / 12).toFixed(3);
                  const increase = ((factor - 1) * 100).toFixed(1);
                  const isHighlighted = calc && Math.round(parseFloat(calc.pitchPer12)) === p;

                  return (
                    <tr
                      key={pitch}
                      className={`border-b border-slate-800/50 transition-colors ${
                        isHighlighted ? 'bg-orange-500/10' : p % 2 === 0 ? 'bg-transparent' : 'bg-slate-800/20'
                      }`}
                    >
                      <td className="px-4 py-2.5 font-medium text-white">{p}/12</td>
                      <td className="px-4 py-2.5 text-center text-slate-300">{angle}&deg;</td>
                      <td className="px-4 py-2.5 text-center text-slate-300">{factor.toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-center text-slate-300">{hipValley}</td>
                      <td className="px-4 py-2.5 text-center text-slate-500 hidden sm:table-cell">+{increase}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            Pitch factor = &radic;(rise&sup2; + 144) / 12 &middot; Hip/valley factor = &radic;(rise&sup2; + 288) / 12 &middot; Angle = atan(rise/12)
          </p>
        </section>

        {/* ── Educational Content ───────────────────────────── */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-6">
            What Is Roof Pitch?
          </h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Roof pitch is the measure of a roof's steepness, expressed as a ratio of the
              vertical rise to the horizontal run. In the United States, pitch is typically written
              as <strong className="text-white">X/12</strong>, meaning the roof rises X inches for
              every 12 inches of horizontal distance.
            </p>
            <p>
              For example, a <strong className="text-white">6/12 pitch</strong> means the roof
              rises 6 inches for every 12 inches of run, creating roughly a 26.6-degree angle.
            </p>

            <h3 className="font-heading font-bold text-xl text-white pt-4">
              Why Does Roof Pitch Matter?
            </h3>
            <ul className="list-disc list-inside space-y-2 text-slate-400">
              <li>
                <strong className="text-slate-200">Material estimation:</strong> Steeper roofs have
                more surface area than their footprint, requiring more shingles, underlayment, and
                other materials.
              </li>
              <li>
                <strong className="text-slate-200">Labor costs:</strong> Steeper pitches (above 7/12)
                typically require additional safety equipment and take longer to install, increasing
                labor rates.
              </li>
              <li>
                <strong className="text-slate-200">Material compatibility:</strong> Some roofing
                materials have minimum and maximum pitch requirements. Asphalt shingles need at
                least a 2/12 pitch, while standing seam metal can go as low as 0.5/12.
              </li>
              <li>
                <strong className="text-slate-200">Water drainage:</strong> Higher pitch means
                faster water runoff and less risk of leaks or ponding.
              </li>
            </ul>

            <h3 className="font-heading font-bold text-xl text-white pt-4">
              How to Measure Roof Pitch
            </h3>
            <p className="text-slate-400">
              You can measure roof pitch from the attic or from the roof surface using a level and
              tape measure. Place a level horizontally and mark 12 inches from one end. Measure the
              vertical distance from the 12-inch mark to the roof surface. That vertical distance
              is your rise per 12 inches of run.
            </p>

            <h3 className="font-heading font-bold text-xl text-white pt-4">
              Understanding the Pitch Factor Multiplier
            </h3>
            <p className="text-slate-400">
              The pitch factor (also called the slope factor) converts a flat, footprint-measured
              roof area into the actual sloped surface area. Multiply your flat area by the pitch
              factor to get the true roof area you need to cover with materials.
            </p>
            <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700 flex items-start gap-3 mt-2">
              <Info className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300">
                <strong className="text-white">Formula:</strong> Actual Roof Area = Flat Area x Pitch Factor.
                For a 2,000 sq ft footprint at 6/12 pitch: 2,000 x 1.118 = 2,236 sq ft of actual roof surface.
              </p>
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────── */}
        <section className="text-center rounded-2xl p-8 sm:p-12 border border-slate-800 relative overflow-hidden"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(249,115,22,0.08) 0%, rgba(15,23,42,0.5) 70%)' }}
        >
          <div className="relative z-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-3">
              Need Full Roof Measurements?
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-6">
              Get instant satellite-based measurements including total area, pitch, ridges, valleys,
              and material lists. Start free with 3 reports per month.
            </p>
            <button
              onClick={() => openAuthModal('register')}
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 rounded-lg font-heading font-bold tracking-wide transition-colors"
            >
              Try SquareCount Free
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* ── Internal Links ─────────────────────────────────── */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-xl text-white mb-4">Related Tools &amp; Resources</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/free-alternative" className="p-4 bg-slate-900/60 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Free Roof Measurement Software</h3>
              <p className="text-slate-500 text-xs">Get full satellite roof measurements — 3 free reports/month.</p>
            </Link>
            <Link to="/blog/roof-pitch-factor-chart-guide" className="p-4 bg-slate-900/60 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Roof Pitch Factor Chart Guide</h3>
              <p className="text-slate-500 text-xs">Printable chart + full explanation of how to use pitch multipliers.</p>
            </Link>
          </div>
        </section>
      </main>

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is a roof pitch factor?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A roof pitch factor (also called pitch multiplier or slope factor) is a number you multiply by the flat, footprint-measured roof area to get the actual sloped surface area. For example, a 6/12 pitch has a factor of 1.118, meaning a 2,000 sq ft footprint has 2,236 sq ft of actual roof surface."
            }
          },
          {
            "@type": "Question",
            "name": "How do you calculate pitch factor?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Pitch factor = sqrt(rise^2 + 144) / 12 for standard X/12 pitches. For example, a 6/12 pitch: sqrt(36 + 144) / 12 = sqrt(180) / 12 = 1.118. The hip/valley factor uses sqrt(rise^2 + 288) / 12 — for 6/12 that is 1.500."
            }
          },
          {
            "@type": "Question",
            "name": "What is the pitch factor for a 6/12 roof?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A 6/12 roof pitch has a pitch factor of 1.118 and an angle of 26.6 degrees. The actual roof surface area is 11.8% larger than the flat footprint area — a 2,000 sq ft footprint at 6/12 pitch equals 2,236 sq ft of actual roof area. Its hip/valley factor is 1.500."
            }
          }
        ]
      }) }} />

      <Footer />
    </div>
  );
}
